import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { FileText, Download, X, Loader2, ExternalLink } from "lucide-react";
import { API_URL } from "./constants";

// Shared classNames so the native <a> action links look identical to the
// previous <Button> components.
const BTN_BASE =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md " +
  "text-sm font-medium transition-colors focus-visible:outline-none h-9 px-4 py-2";
const BTN_PRIMARY =
  BTN_BASE + " bg-[#42E695] text-[#04042C] hover:bg-[#42E695]/80";
const BTN_OUTLINE =
  BTN_BASE + " border border-[#1E1E5F] text-slate-300 hover:bg-[#1E1E5F]";
const BTN_DISABLED =
  BTN_BASE + " bg-[#42E695]/40 text-[#04042C]/40 cursor-not-allowed pointer-events-none";

export const QuotePdfDialog = ({ open, onOpenChange, selectedOpp }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [blob, setBlob] = useState(null);
  const [httpLinks, setHttpLinks] = useState(null); // { download_url, inline_url }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [popupWarn, setPopupWarn] = useState(false);
  const urlRef = useRef(null); // tracks the live URL for revoke-on-unmount

  // Fetch PDF as a blob whenever the dialog opens for a different opportunity.
  // In parallel, request short-lived HTTP links so Descargar/Abrir keep working
  // in environments where blob: URLs are blocked (sandboxed preview iframes).
  useEffect(() => {
    if (!open || !selectedOpp) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setPopupWarn(false);
    setBlobUrl(null);
    setBlob(null);
    setHttpLinks(null);

    // Issue signed HTTP links first — even if the blob fetch fails or the
    // preview can't render, the action buttons below will still work.
    axios
      .post(`${API_URL}/api/opportunities/${selectedOpp.id}/quote-link`)
      .then((res) => { if (!cancelled) setHttpLinks(res.data || null); })
      .catch(() => { /* non-fatal — buttons will fall back to blob if present */ });

    axios
      .post(
        `${API_URL}/api/opportunities/${selectedOpp.id}/generate-quote`,
        {},
        { responseType: "blob" }
      )
      .then((res) => {
        if (cancelled) return;
        if (!res.data || res.data.size === 0) {
          setError("El presupuesto generado está vacío");
          return;
        }
        const pdfBlob = new Blob([res.data], { type: "application/pdf" });
        const url = URL.createObjectURL(pdfBlob);
        // Revoke the previous URL (if any) — now safe because state was reset.
        if (urlRef.current) {
          try { URL.revokeObjectURL(urlRef.current); } catch (e) { /* noop */ }
        }
        urlRef.current = url;
        setBlob(pdfBlob);
        setBlobUrl(url);
      })
      .catch(async (err) => {
        if (cancelled) return;
        let msg = "No se pudo generar el presupuesto";
        const status = err?.response?.status;
        try {
          const data = err?.response?.data;
          if (data instanceof Blob) {
            const text = await data.text();
            if (text) msg = text.slice(0, 200);
          } else if (typeof data === "string" && data) {
            msg = data.slice(0, 200);
          }
        } catch (e) { /* noop */ }
        setError(status ? `${msg} (HTTP ${status})` : msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, selectedOpp?.id]);

  // Revoke the live blob URL only when the dialog closes. Keeps the URL alive
  // the whole time the user might be clicking Descargar / Abrir.
  useEffect(() => {
    if (!open && urlRef.current) {
      const u = urlRef.current;
      urlRef.current = null;
      // Small delay so any pending click-triggered navigation completes first.
      setTimeout(() => { try { URL.revokeObjectURL(u); } catch (e) { /* noop */ } }, 1500);
    }
  }, [open]);

  const filename = `presupuesto-${selectedOpp?.opportunity_id || "oferta"}.pdf`;

  // Belt-and-braces: if the sandbox blocks anchor navigation, we still have a
  // JS fallback that fires programmatically. Bound to the anchor's onClick so
  // it runs *in addition to* the native behaviour — harmless if both succeed.
  const triggerProgrammaticDownload = (e) => {
    if (!blob) return;
    try {
      // Legacy Edge / IE11
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        e.preventDefault();
        window.navigator.msSaveOrOpenBlob(blob, filename);
      }
      // Modern browsers already honour the <a download> attribute.
    } catch (err) { /* noop */ }
  };

  const onOpenNewTabClick = (e) => {
    // Detect popup-blocker / sandbox without allow-popups and show a visible
    // hint instead of failing silently.
    setTimeout(() => {
      try {
        // Heuristic: if document lost focus, a popup likely opened.
        if (document.hasFocus()) setPopupWarn(true);
        else setPopupWarn(false);
      } catch (err) { /* noop */ }
    }, 600);
  };

  // Prefer the HTTP signed links (work in sandboxed iframes, survive blob:
  // restrictions). Fall back to blob URLs when the link-issuing endpoint
  // hasn't returned yet (keeps old behaviour alive in production too).
  const absoluteDownloadHref = httpLinks?.download_url
    ? `${API_URL}${httpLinks.download_url}`
    : blobUrl;
  const absoluteInlineHref = httpLinks?.inline_url
    ? `${API_URL}${httpLinks.inline_url}`
    : blobUrl;
  const actionsReady = !!(httpLinks || (blobUrl && blob));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0F0F3D] border-[#1E1E5F] text-white p-0 gap-0 flex flex-col"
        style={{ maxWidth: "90vw", width: "90vw", height: "90vh", maxHeight: "90vh" }}
        data-testid="quote-pdf-modal"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Presupuesto PDF</DialogTitle>
          <DialogDescription>Vista previa del presupuesto generado</DialogDescription>
        </DialogHeader>

        {/* Header bar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#1E1E5F] shrink-0">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#03BEE4]" />
            <span className="text-lg font-semibold text-[#03BEE4]">
              Presupuesto {selectedOpp?.opportunity_id}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {actionsReady ? (
              <a
                data-testid="pdf-open-tab-btn"
                href={absoluteInlineHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onOpenNewTabClick}
                className={BTN_OUTLINE}
                title="Abrir en pestaña nueva"
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                Abrir
              </a>
            ) : null}
            {actionsReady ? (
              <a
                data-testid="pdf-download-btn"
                href={absoluteDownloadHref}
                download={filename}
                onClick={triggerProgrammaticDownload}
                className={BTN_PRIMARY}
              >
                <Download className="h-4 w-4 mr-1" />
                Descargar
              </a>
            ) : (
              <span data-testid="pdf-download-btn" className={BTN_DISABLED}>
                <Download className="h-4 w-4 mr-1" />
                Descargar
              </span>
            )}
            <Button
              data-testid="pdf-close-btn"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-[#1E1E5F] text-slate-300 hover:bg-[#1E1E5F]"
            >
              <X className="h-4 w-4 mr-2" />
              Cerrar
            </Button>
          </div>
        </div>

        {/* PDF viewer area */}
        <div className="relative flex-1 min-h-0">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#0F0F3D] z-10">
              <Loader2 className="h-8 w-8 animate-spin text-[#03BEE4]" />
              <span className="ml-3 text-slate-300">Generando presupuesto...</span>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0F0F3D] z-10 px-4 text-center">
              <FileText className="h-10 w-10 text-red-400/70 mb-3" />
              <span className="text-red-400 text-lg mb-1">{error}</span>
              <span className="text-slate-400 text-sm">Intenta cerrar y volver a abrir el presupuesto.</span>
            </div>
          )}
          {!loading && !error && !blobUrl && !actionsReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#0F0F3D] z-10 text-slate-400 text-sm">
              PDF no disponible todavía.
            </div>
          )}
          {/* Preview area: try to embed the blob PDF (fast, no extra HTTP hop).
              If the environment refuses to render it (sandboxed iframes, no
              native PDF plugin) the <object> fallback shows a message plus
              the same HTTP-backed action buttons. */}
          {blobUrl && !error && (
            <object
              data={blobUrl}
              type="application/pdf"
              className="w-full h-full"
              data-testid="pdf-object"
              style={{ background: "#fff" }}
            >
              <embed
                src={blobUrl}
                type="application/pdf"
                className="w-full h-full"
                data-testid="pdf-embed"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0F0F3D] px-4 text-center">
                <FileText className="h-10 w-10 text-[#03BEE4]/70 mb-3" />
                <span className="text-slate-300 mb-3">
                  No se puede previsualizar el PDF en este entorno. Usa los botones de abajo.
                </span>
                <div className="flex gap-2">
                  <a
                    href={absoluteInlineHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onOpenNewTabClick}
                    className={BTN_OUTLINE}
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Abrir en nueva pestaña
                  </a>
                  <a
                    href={absoluteDownloadHref}
                    download={filename}
                    onClick={triggerProgrammaticDownload}
                    className={BTN_PRIMARY}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Descargar
                  </a>
                </div>
                {popupWarn && (
                  <span className="text-xs text-yellow-400 mt-3 max-w-md">
                    La apertura en nueva pestaña puede estar bloqueada por el navegador o
                    por este entorno de previsualización. Pulsa "Descargar" para obtener
                    el PDF.
                  </span>
                )}
              </div>
            </object>
          )}
          {/* If the blob fetch failed but HTTP links are ready, still offer the
              full fallback screen so the user can download via HTTP. */}
          {!blobUrl && !loading && !error && actionsReady && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0F0F3D] px-4 text-center">
              <FileText className="h-10 w-10 text-[#03BEE4]/70 mb-3" />
              <span className="text-slate-300 mb-3">
                No se puede previsualizar el PDF en este entorno. Usa los botones de abajo.
              </span>
              <div className="flex gap-2">
                <a href={absoluteInlineHref} target="_blank" rel="noopener noreferrer"
                   onClick={onOpenNewTabClick} className={BTN_OUTLINE}>
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Abrir en nueva pestaña
                </a>
                <a href={absoluteDownloadHref} download={filename}
                   onClick={triggerProgrammaticDownload} className={BTN_PRIMARY}>
                  <Download className="h-4 w-4 mr-1" />
                  Descargar
                </a>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
